/**
 * TC-2: frontend half of the acknowledged-transaction protocol (see
 * TOOL_CONSOLIDATION_ARCHITECTURE.md in the backend repo).
 *
 * When CLASSIFIER_ONLY_DETECTION is enabled, the backend stamps its
 * authoritative events (start_coding_question / end_coding_question /
 * interview_completed) with a `txn_id` and re-emits them until the frontend
 * replies `{ type: 'event_ack', txn_id }`. The frontend must therefore:
 *   - ACK every receipt (including idempotent re-emits) so the backend stops
 *     retrying;
 *   - APPLY the event exactly once, so a re-emit doesn't re-open/re-close the
 *     panel or double-finalize.
 *
 * VA-27638 — two changes to that contract:
 *
 * 1. **Dedupe on the SEMANTIC key, not only the txn_id.** Deduping by txn_id
 *    alone only catches re-emits of the *same send*. A retry of one coding-open
 *    and a freshly-emitted open for the *same question* carry different
 *    txn_ids, so both applied — and because each `problem_statement` open
 *    remounts the editor, the second one wiped whatever the candidate had
 *    typed. The backend now stamps a `dedupe_key` describing what the event
 *    MEANS; anything already applied under that key is a duplicate however it
 *    arrived.
 *
 * 2. **Report what we actually DID, not just that we received it.** The ack is
 *    sent before the caller decides whether it can render, so the backend could
 *    hold an ack for an event that never became an editor — and then believe
 *    there was nothing to recover. `event_applied` carries the real outcome.
 *
 * When the flag is OFF the events carry no `txn_id`, so `classifyTrackedEvent`
 * reports `tracked: false` and the caller behaves exactly as before — no ack,
 * no dedupe. This module is pure so it can be unit-tested in isolation.
 */
export const TXN_DEDUPE_MAX = 200;
export function createSeenEvents() {
    return { txns: new Set(), keys: new Set() };
}
/** Decide how to handle a possibly-tracked backend event. Read-only: the caller
 * records it via {@link rememberTxn} once it commits to applying. */
export function classifyTrackedEvent(txnId, seen, dedupeKey) {
    const id = typeof txnId === 'string' && txnId ? txnId : null;
    if (!id) {
        return { tracked: false, shouldAck: false, duplicate: false };
    }
    // Tolerate the legacy bare-Set shape so this can be adopted incrementally.
    const txns = seen instanceof Set ? seen : seen.txns;
    const keys = seen instanceof Set ? null : seen.keys;
    const key = typeof dedupeKey === 'string' && dedupeKey ? dedupeKey : null;
    const duplicate = txns.has(id) || Boolean(key && (keys === null || keys === void 0 ? void 0 : keys.has(key)));
    return { tracked: true, shouldAck: true, duplicate };
}
/** Record an event as applied, keeping the sets bounded (a cleared set is safe —
 * the backend never re-emits an already-acked txn_id, and txn_ids are uuids so
 * an old one can't recur). */
export function rememberTxn(txnId, seen, dedupeKey) {
    const txns = seen instanceof Set ? seen : seen.txns;
    txns.add(txnId);
    if (txns.size > TXN_DEDUPE_MAX) {
        txns.clear();
        txns.add(txnId);
    }
    if (!(seen instanceof Set) && typeof dedupeKey === 'string' && dedupeKey) {
        seen.keys.add(dedupeKey);
        if (seen.keys.size > TXN_DEDUPE_MAX) {
            seen.keys.clear();
            seen.keys.add(dedupeKey);
        }
    }
}
/** Forget a dedupe key so the same intent may be applied again. Used when a
 * coding question genuinely reopens (a skip followed by a candidate-driven
 * reopen is a NEW intent, not a duplicate of the original open). */
export function forgetDedupeKey(dedupeKey, seen) {
    if (seen instanceof Set)
        return;
    if (typeof dedupeKey === 'string' && dedupeKey)
        seen.keys.delete(dedupeKey);
}
